/**
 * Reading and writing the option values of an automation field, in the format
 * the value kind of the field gives them.
 *
 * An id is unique and stable, so a selection of ids is stored comma separated.
 * A name is the text of the option, which may itself contain a comma, so a
 * selection of names is stored as a JSON array and a single name verbatim. Two
 * names are the same option when they read the same, whatever their padding
 * and case, mirroring how the api compares them.
 */

import { AutomationValueKind } from '@models/automation-rules';
import { SelectOption } from '@models/shared.model';

/**
 * Whether the values of the field are the names of its options. A field the api
 * sends no kind for keeps the id based values it was stored with.
 */
export function isNameBasedValueKind(
    valueKind: AutomationValueKind | null | undefined,
): boolean {
    return valueKind === AutomationValueKind.Name;
}

/**
 * The form two values of the kind are compared in, so values differing only in
 * padding, or in case for a name, collapse into one.
 */
export function toOptionComparisonValue(
    value: string,
    valueKind: AutomationValueKind | null | undefined,
): string {
    const trimmed = value.trim();

    return isNameBasedValueKind(valueKind) ? trimmed.toLowerCase() : trimmed;
}

export function optionValuesEqual(
    left: string,
    right: string,
    valueKind: AutomationValueKind | null | undefined,
): boolean {
    return (
        toOptionComparisonValue(left, valueKind) ===
        toOptionComparisonValue(right, valueKind)
    );
}

/**
 * Whether two multi select values hold the same combination of options,
 * whatever their order, padding and repetition: a selection is a set, while
 * the text carrying it is built in selection order on the screen and in
 * canonical order by the api.
 */
export function optionValueSetsEqual(
    left: string,
    right: string,
    valueKind: AutomationValueKind | null | undefined,
): boolean {
    const leftKeys = optionComparisonKeys(left, valueKind);
    const rightKeys = optionComparisonKeys(right, valueKind);

    return (
        leftKeys.length === rightKeys.length &&
        leftKeys.every((key, index) => key === rightKeys[index])
    );
}

/**
 * The distinct options a stored multi select value holds, in an order of their
 * own, so a comparison holds whatever order the text carries them in.
 */
function optionComparisonKeys(
    value: string,
    valueKind: AutomationValueKind | null | undefined,
): string[] {
    const keys = parseOptionValues(value, valueKind)
        .map((option) => toOptionComparisonValue(option, valueKind))
        .filter((key) => key !== '');

    return [...new Set(keys)].sort((left, right) => {
        if (left === right) return 0;
        return left < right ? -1 : 1;
    });
}

/**
 * The single values a stored multi select value holds. A name based value that
 * is not a JSON array is read as the single name it is, so a value stored
 * before the array format existed keeps working.
 */
export function parseOptionValues(
    value: string | null | undefined,
    valueKind: AutomationValueKind | null | undefined,
): string[] {
    const text = (value ?? '').trim();
    if (text === '') return [];

    if (!isNameBasedValueKind(valueKind)) {
        return text
            .split(',')
            .map((part) => part.trim())
            .filter((part) => part !== '');
    }

    if (!text.startsWith('[')) return [text];

    return parseNameArray(text) ?? [text];
}

/**
 * What the api stores for a multi select selection, in the canonical order and
 * format of the kind, so the same selection is always the same text and a rule
 * value can be compared to it as it stands.
 */
export function serializeOptionValues(
    values: readonly (string | number | null | undefined)[],
    valueKind: AutomationValueKind | null | undefined,
): string {
    const canonical = canonicalOptionValues(values, valueKind);
    if (canonical.length === 0) return '';

    return isNameBasedValueKind(valueKind)
        ? JSON.stringify(canonical)
        : canonical.join(',');
}

/**
 * The text of a stored value, so a name based multi select reads as its names
 * rather than as the JSON array holding them.
 */
export function formatOptionValues(
    value: string | null | undefined,
    valueKind: AutomationValueKind | null | undefined,
): string {
    return parseOptionValues(value, valueKind).join(', ');
}

/** Distinct, ordered values of a selection, as the api canonicalizes them. */
export function canonicalOptionValues(
    values: readonly (string | number | null | undefined)[],
    valueKind: AutomationValueKind | null | undefined,
): string[] {
    const seen = new Set<string>();
    const distinct: string[] = [];

    for (const value of values) {
        if (value == null) continue;

        const text = String(value).trim();
        if (text === '') continue;

        const key = toOptionComparisonValue(text, valueKind);
        if (seen.has(key)) continue;

        seen.add(key);
        distinct.push(text);
    }

    return sortOptionValues(distinct, valueKind);
}

/**
 * The option of the currently loaded data a rule value stands for, or
 * `undefined` when the data offers none: rules are configured over every
 * repository, so a value can be valid and still have no option here.
 *
 * A name is matched against the text of the option, an id against its value.
 */
export function findOptionByRuleValue(
    options: readonly SelectOption[],
    ruleValue: string,
    valueKind: AutomationValueKind | null | undefined,
): SelectOption | undefined {
    const wanted = toOptionComparisonValue(ruleValue, valueKind);
    if (wanted === '') return undefined;

    return options.find(
        (option) =>
            toOptionComparisonValue(
                isNameBasedValueKind(valueKind)
                    ? String(option.viewValue)
                    : String(option.value),
                valueKind,
            ) === wanted,
    );
}

/**
 * The value the rules of the field express what a control holds, or `null`
 * when the loaded options hold no such option and the value therefore cannot
 * be expressed as a rule value at all.
 */
export function toRuleOptionValue(
    options: readonly SelectOption[],
    controlValue: string | number,
    valueKind: AutomationValueKind | null | undefined,
): string | null {
    const text = String(controlValue).trim();
    if (text === '') return null;
    if (!isNameBasedValueKind(valueKind)) return text;

    const option = options.find(
        (candidate) => String(candidate.value) === text,
    );

    return option ? String(option.viewValue).trim() : null;
}

/** The names of a JSON array, or `null` when the text is not one. */
function parseNameArray(text: string): string[] | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }

    if (!Array.isArray(parsed)) return null;

    return parsed
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
}

function sortOptionValues(
    values: string[],
    valueKind: AutomationValueKind | null | undefined,
): string[] {
    if (isNameBasedValueKind(valueKind)) {
        return [...values].sort((left, right) => {
            const a = left.toLowerCase();
            const b = right.toLowerCase();
            if (a === b) return 0;
            return a < b ? -1 : 1;
        });
    }

    // Ids are ordered numerically, so "12,7" and "7,12" are one combination.
    if (values.every((value) => /^-?\d+$/.test(value))) {
        return [...values].sort((left, right) => Number(left) - Number(right));
    }

    return values;
}
